import React, { useState, useRef, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X, ChevronDown, Newspaper, User, GraduationCap, Mic, FileText, Video, MessageCircle, Phone } from 'lucide-react';

const DROPDOWN_CLOSE_DELAY_MS = 200;

export const Header = () => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [contentDropdownOpen, setContentDropdownOpen] = useState(false);
  const closeDropdownTimeoutRef = useRef(null);
  const location = useLocation();

  const cancelCloseDropdown = () => {
    if (closeDropdownTimeoutRef.current) {
      clearTimeout(closeDropdownTimeoutRef.current);
      closeDropdownTimeoutRef.current = null;
    }
  };

  const scheduleCloseDropdown = () => {
    cancelCloseDropdown();
    closeDropdownTimeoutRef.current = setTimeout(() => {
      setContentDropdownOpen(false);
      closeDropdownTimeoutRef.current = null;
    }, DROPDOWN_CLOSE_DELAY_MS);
  };

  const openDropdown = () => {
    cancelCloseDropdown();
    setContentDropdownOpen(true);
  };

  useEffect(() => {
    return () => cancelCloseDropdown();
  }, []);

  const mainLinks = [
    { label: 'Notícias', path: '/', icon: Newspaper },
    { label: 'Sobre', path: '/sobre', icon: User }
  ];

  const contentLinks = [
    { label: 'Cursos', path: '/cursos', icon: GraduationCap },
    { label: 'Podcast', path: '/podcast', icon: Mic },
    { label: 'Atualizações Legislativas', path: '/atualizacoes-legislativas', icon: FileText },
    { label: 'Entrevistas', path: '/entrevistas', icon: Video },
    { label: 'Bate papo do Direito', path: '/bate-papo', icon: MessageCircle }
  ];

  const contactLink = { label: 'Contato', path: '/contato', icon: Phone };

  return (
    <header className="sticky top-0 z-50 bg-[#0F172A] shadow-lg backdrop-blur-sm bg-opacity-95">
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <div className="flex items-center justify-between h-20">
          {/* Logo */}
          <Link to="/" className="flex-shrink-0">
            <h1 className="text-2xl lg:text-3xl font-serif text-[#B49163] font-bold tracking-wide hover:text-[#9a7a52] transition-all duration-300 hover:scale-105">
              Universo do Direito
            </h1>
          </Link>
          
          {/* Desktop Navigation */}
          <nav className="hidden lg:flex items-center space-x-1">
            {/* Main Links */}
            {mainLinks.map((link) => {
              const IconComponent = link.icon;
              return (
                <Link
                  key={link.path}
                  to={link.path}
                  className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-all duration-300 ${
                    location.pathname === link.path 
                      ? 'bg-[#B49163] text-white' 
                      : 'text-gray-300 hover:bg-[#1e293b] hover:text-white'
                  }`}
                >
                  <IconComponent size={18} />
                  {link.label}
                </Link>
              );
            })}

            {/* Content Dropdown */}
            <div 
              className="relative"
              onMouseEnter={openDropdown}
              onMouseLeave={scheduleCloseDropdown}
            >
              <button className="flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium text-gray-300 hover:bg-[#1e293b] hover:text-white transition-all duration-300">
                Conteúdos
                <ChevronDown size={16} className={`transition-transform duration-300 ${contentDropdownOpen ? 'rotate-180' : ''}`} />
              </button>
              
              {/* Dropdown Menu - onMouseEnter cancela o fechamento ao atravessar o gap */}
              {contentDropdownOpen && (
                <div 
                  className="absolute top-full left-0 mt-2 w-64 bg-[#1e293b] rounded-xl shadow-2xl border border-gray-700 overflow-hidden"
                  onMouseEnter={openDropdown}
                >
                  {contentLinks.map((link) => {
                    const IconComponent = link.icon;
                    return (
                      <Link
                        key={link.path}
                        to={link.path}
                        className={`flex items-center gap-3 px-5 py-3 text-sm font-medium transition-all duration-200 ${
                          location.pathname === link.path
                            ? 'bg-[#B49163] text-white'
                            : 'text-gray-300 hover:bg-[#334155] hover:text-white'
                        }`}
                      >
                        <IconComponent size={18} />
                        {link.label}
                      </Link>
                    );
                  })}
                </div>
              )}
            </div>

            {/* Contact Link */}
            <Link
              to={contactLink.path}
              className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-all duration-300 ${
                location.pathname === contactLink.path 
                  ? 'bg-[#B49163] text-white' 
                  : 'text-gray-300 hover:bg-[#1e293b] hover:text-white'
              }`}
            >
              <contactLink.icon size={18} />
              {contactLink.label}
            </Link>
          </nav>
          
          {/* Mobile Menu Button */}
          <button 
            className="lg:hidden p-2 text-white hover:text-[#B49163] transition-colors duration-300"
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
          >
            {mobileMenuOpen ? <X size={28} /> : <Menu size={28} />}
          </button>
        </div>
      </div>
      
      {/* Mobile Navigation */}
      {mobileMenuOpen && (
        <div className="lg:hidden bg-[#1e293b] border-t border-gray-700">
          <nav className="px-6 py-4 space-y-1">
            {/* Main Links Mobile */}
            {mainLinks.map((link) => {
              const IconComponent = link.icon;
              return (
                <Link
                  key={link.path}
                  to={link.path}
                  onClick={() => setMobileMenuOpen(false)}
                  className={`flex items-center gap-3 px-4 py-3 rounded-lg text-sm font-medium transition-all duration-200 ${
                    location.pathname === link.path
                      ? 'bg-[#B49163] text-white'
                      : 'text-gray-300 hover:bg-[#334155] hover:text-white'
                  }`}
                >
                  <IconComponent size={18} />
                  {link.label}
                </Link>
              );
            })}

            {/* Content Links Mobile */}
            <div className="py-2">
              <p className="px-4 text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2">
                Conteúdos
              </p>
              {contentLinks.map((link) => {
                const IconComponent = link.icon;
                return (
                  <Link
                    key={link.path}
                    to={link.path}
                    onClick={() => setMobileMenuOpen(false)}
                    className={`flex items-center gap-3 px-4 py-3 rounded-lg text-sm font-medium transition-all duration-200 ${
                      location.pathname === link.path
                        ? 'bg-[#B49163] text-white'
                        : 'text-gray-300 hover:bg-[#334155] hover:text-white'
                    }`}
                  >
                    <IconComponent size={18} />
                    {link.label}
                  </Link>
                );
              })}
            </div>

            {/* Contact Link Mobile */}
            <Link
              to={contactLink.path}
              onClick={() => setMobileMenuOpen(false)}
              className={`flex items-center gap-3 px-4 py-3 rounded-lg text-sm font-medium transition-all duration-200 ${
                location.pathname === contactLink.path
                  ? 'bg-[#B49163] text-white'
                  : 'text-gray-300 hover:bg-[#334155] hover:text-white'
              }`}
            >
              <contactLink.icon size={18} />
              {contactLink.label}
            </Link>
          </nav>
        </div>
      )}
    </header>
  );
};

export default Header;